/**
 * cmsSlice — Redux Toolkit slice for admin CMS page management.
 *
 * State shape
 * -----------
 * pages:
 *   - list: CmsPageListItemDTO[]  — current page of CMS pages (list view)
 *   - pagination: CmsPagePaginationMeta | null
 * editor:
 *   - page: CmsPageEditDTO | null  — page currently open in the editor
 *   - revisions: CmsPageRevisionDTO[]  — lazy-loaded revision history
 * loading:
 *   - fetchPages / updatePage / deletePage / duplicatePage (list view)
 *   - fetchPage / createPage / savePage / fetchRevisions (editor)
 * error: matching string | null per key
 *
 * Thunks
 * ------
 * - fetchPages(params?) — GET /api/v1/admin/cms/pages
 * - updatePage({ id, ...payload }) — PUT /api/v1/admin/cms/pages/:id (status toggle)
 * - deletePage(id) — DELETE /api/v1/admin/cms/pages/:id
 * - duplicatePage(id) — POST /api/v1/admin/cms/pages/:id/duplicate
 * - fetchPage(id) — GET /api/v1/admin/cms/pages/:id (CON-CMS-002)
 * - createPage(payload) — POST /api/v1/admin/cms/pages (CON-CMS-002)
 * - savePage({ id, ...payload }) — PUT /api/v1/admin/cms/pages/:id full (CON-CMS-002)
 * - fetchRevisions(id) — GET /api/v1/admin/cms/pages/:id/revisions (CON-CMS-002)
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only the page-level containers do.
 *
 * CON-CMS-001 / CON-CMS-002.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  cmsAdminApiClient,
  type UpdateCmsPagePayload,
} from '@dfq/shared/api/admin/cms';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  CmsPageEditDTO,
  CmsPageListItemDTO,
  CmsPagePaginationMeta,
  CmsPageRevisionDTO,
  CreateCmsPagePayload,
  FetchCmsPagesParams,
  UpdateCmsPageFullPayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type CmsLoadingKey =
  | 'fetchPages'
  | 'updatePage'
  | 'deletePage'
  | 'duplicatePage'
  | 'fetchPage'
  | 'createPage'
  | 'savePage'
  | 'fetchRevisions';

/** Rejection payload for thunks whose caller needs per-field 422 errors (SF-DSVC-103). */
interface FieldValidationRejection {
  message: string;
  errors?: Record<string, string[]>;
}

export interface CmsState {
  pages: {
    list: CmsPageListItemDTO[];
    pagination: CmsPagePaginationMeta | null;
  };
  editor: {
    page: CmsPageEditDTO | null;
    revisions: CmsPageRevisionDTO[];
  };
  loading: Record<CmsLoadingKey, boolean>;
  error: Record<CmsLoadingKey, string | null>;
}

const initialState: CmsState = {
  pages: {
    list: [],
    pagination: null,
  },
  editor: {
    page: null,
    revisions: [],
  },
  loading: {
    fetchPages: false,
    updatePage: false,
    deletePage: false,
    duplicatePage: false,
    fetchPage: false,
    createPage: false,
    savePage: false,
    fetchRevisions: false,
  },
  error: {
    fetchPages: null,
    updatePage: null,
    deletePage: null,
    duplicatePage: null,
    fetchPage: null,
    createPage: null,
    savePage: null,
    fetchRevisions: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchPages = createAsyncThunk(
  'cms/fetchPages',
  async (params: FetchCmsPagesParams | void) => {
    return cmsAdminApiClient.fetchPages(params ?? {});
  },
);

export const updatePage = createAsyncThunk(
  'cms/updatePage',
  async (
    { id, ...payload }: { id: number } & UpdateCmsPagePayload,
    { rejectWithValue },
  ) => {
    try {
      return await cmsAdminApiClient.updatePage(id, payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ id, message: err.message });
      }
      const msg = err instanceof Error ? err.message : 'Failed to update page';
      return rejectWithValue({ id, message: msg });
    }
  },
);

export const deletePage = createAsyncThunk(
  'cms/deletePage',
  async (id: number, { rejectWithValue }) => {
    try {
      await cmsAdminApiClient.deletePage(id);
      return id;
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err.message);
      }
      const msg = err instanceof Error ? err.message : 'Failed to delete page';
      return rejectWithValue(msg);
    }
  },
);

export const duplicatePage = createAsyncThunk(
  'cms/duplicatePage',
  async (id: number, { rejectWithValue }) => {
    try {
      return await cmsAdminApiClient.duplicatePage(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, status: err.status });
      }
      const msg = err instanceof Error ? err.message : 'Failed to duplicate page';
      return rejectWithValue({ message: msg, status: 0 });
    }
  },
);

// -- Editor thunks (CON-CMS-002) --------------------------------------------

export const fetchPage = createAsyncThunk(
  'cms/fetchPage',
  async (id: number, { rejectWithValue }) => {
    try {
      return await cmsAdminApiClient.fetchPage(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err.message);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch page';
      return rejectWithValue(msg);
    }
  },
);

export const createPage = createAsyncThunk<
  CmsPageEditDTO,
  CreateCmsPagePayload,
  { rejectValue: FieldValidationRejection }
>('cms/createPage', async (payload, { rejectWithValue }) => {
  try {
    return await cmsAdminApiClient.createPage(payload);
  } catch (err) {
    if (isApiError(err)) {
      return rejectWithValue({ message: err.message, errors: err.errors });
    }
    const msg = err instanceof Error ? err.message : 'Failed to create page';
    return rejectWithValue({ message: msg });
  }
});

export const savePage = createAsyncThunk<
  CmsPageEditDTO,
  { id: number } & UpdateCmsPageFullPayload,
  { rejectValue: FieldValidationRejection }
>('cms/savePage', async ({ id, ...payload }, { rejectWithValue }) => {
  try {
    return await cmsAdminApiClient.updatePageFull(id, payload);
  } catch (err) {
    if (isApiError(err)) {
      return rejectWithValue({ message: err.message, errors: err.errors });
    }
    const msg = err instanceof Error ? err.message : 'Failed to save page';
    return rejectWithValue({ message: msg });
  }
});

export const fetchRevisions = createAsyncThunk(
  'cms/fetchRevisions',
  async (id: number, { rejectWithValue }) => {
    try {
      return await cmsAdminApiClient.fetchRevisions(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err.message);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch revisions';
      return rejectWithValue(msg);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const cmsSlice = createSlice({
  name: 'cms',
  initialState,
  reducers: {
    /**
     * Optimistically patch a single page in the list. Reverting is done by
     * re-applying the previous state snapshot stored by `updatePage.pending`.
     */
    patchPage(state, action: PayloadAction<CmsPageListItemDTO>) {
      const idx = state.pages.list.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) {
        state.pages.list[idx] = action.payload;
      }
    },

    /** Clear the editor state when leaving the edit view. */
    clearEditor(state) {
      state.editor.page = null;
      state.editor.revisions = [];
      state.error.fetchPage = null;
      state.error.savePage = null;
      state.error.createPage = null;
      state.error.fetchRevisions = null;
    },
  },
  extraReducers: (builder) => {
    // ── fetchPages ──────────────────────────────────────────────────────────
    builder
      .addCase(fetchPages.pending, (state) => {
        state.loading.fetchPages = true;
        state.error.fetchPages = null;
      })
      .addCase(fetchPages.fulfilled, (state, action) => {
        state.loading.fetchPages = false;
        state.pages.list = action.payload.items;
        state.pages.pagination = action.payload.meta;
      })
      .addCase(fetchPages.rejected, (state, action) => {
        state.loading.fetchPages = false;
        state.error.fetchPages = action.error.message ?? 'Failed to fetch pages';
      });

    // ── updatePage ──────────────────────────────────────────────────────────
    builder
      .addCase(updatePage.pending, (state) => {
        state.loading.updatePage = true;
        state.error.updatePage = null;
      })
      .addCase(updatePage.fulfilled, (state, action) => {
        state.loading.updatePage = false;
        const idx = state.pages.list.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) {
          state.pages.list[idx] = action.payload;
        }
      })
      .addCase(updatePage.rejected, (state, action) => {
        state.loading.updatePage = false;
        state.error.updatePage =
          (action.payload as { message: string } | undefined)?.message ??
          action.error.message ??
          'Failed to update page';
      });

    // ── deletePage ──────────────────────────────────────────────────────────
    builder
      .addCase(deletePage.pending, (state) => {
        state.loading.deletePage = true;
        state.error.deletePage = null;
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.loading.deletePage = false;
        state.pages.list = state.pages.list.filter((p) => p.id !== action.payload);
        if (state.pages.pagination) {
          state.pages.pagination = {
            ...state.pages.pagination,
            total: Math.max(0, state.pages.pagination.total - 1),
          };
        }
      })
      .addCase(deletePage.rejected, (state, action) => {
        state.loading.deletePage = false;
        state.error.deletePage =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete page';
      });

    // ── duplicatePage ────────────────────────────────────────────────────────
    builder
      .addCase(duplicatePage.pending, (state) => {
        state.loading.duplicatePage = true;
        state.error.duplicatePage = null;
      })
      .addCase(duplicatePage.fulfilled, (state, action) => {
        state.loading.duplicatePage = false;
        // New page prepended to top of list (platform appends "(Copy)" to title).
        state.pages.list = [action.payload, ...state.pages.list];
        if (state.pages.pagination) {
          state.pages.pagination = {
            ...state.pages.pagination,
            total: state.pages.pagination.total + 1,
          };
        }
      })
      .addCase(duplicatePage.rejected, (state, action) => {
        state.loading.duplicatePage = false;
        const payload = action.payload as { message: string; status: number } | undefined;
        state.error.duplicatePage =
          payload?.message ?? action.error.message ?? 'Failed to duplicate page';
      });

    // ── fetchPage (CON-CMS-002) ─────────────────────────────────────────────
    builder
      .addCase(fetchPage.pending, (state) => {
        state.loading.fetchPage = true;
        state.error.fetchPage = null;
      })
      .addCase(fetchPage.fulfilled, (state, action) => {
        state.loading.fetchPage = false;
        state.editor.page = action.payload;
      })
      .addCase(fetchPage.rejected, (state, action) => {
        state.loading.fetchPage = false;
        state.error.fetchPage =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch page';
      });

    // ── createPage (CON-CMS-002) ────────────────────────────────────────────
    builder
      .addCase(createPage.pending, (state) => {
        state.loading.createPage = true;
        state.error.createPage = null;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.loading.createPage = false;
        state.editor.page = action.payload;
      })
      .addCase(createPage.rejected, (state, action) => {
        state.loading.createPage = false;
        state.error.createPage =
          action.payload?.message ?? action.error.message ?? 'Failed to create page';
      });

    // ── savePage (CON-CMS-002) ──────────────────────────────────────────────
    builder
      .addCase(savePage.pending, (state) => {
        state.loading.savePage = true;
        state.error.savePage = null;
      })
      .addCase(savePage.fulfilled, (state, action) => {
        state.loading.savePage = false;
        state.editor.page = action.payload;
      })
      .addCase(savePage.rejected, (state, action) => {
        state.loading.savePage = false;
        state.error.savePage =
          action.payload?.message ?? action.error.message ?? 'Failed to save page';
      });

    // ── fetchRevisions (CON-CMS-002) ────────────────────────────────────────
    builder
      .addCase(fetchRevisions.pending, (state) => {
        state.loading.fetchRevisions = true;
        state.error.fetchRevisions = null;
      })
      .addCase(fetchRevisions.fulfilled, (state, action) => {
        state.loading.fetchRevisions = false;
        state.editor.revisions = action.payload;
      })
      .addCase(fetchRevisions.rejected, (state, action) => {
        state.loading.fetchRevisions = false;
        state.error.fetchRevisions =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch revisions';
      });
  },
});

export const { patchPage, clearEditor } = cmsSlice.actions;
export const cmsReducer = cmsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface CmsRootState {
  cms: CmsState;
}

export const selectPages = (state: CmsRootState): CmsPageListItemDTO[] =>
  state.cms.pages.list;

export const selectCmsPagination = (
  state: CmsRootState,
): CmsPagePaginationMeta | null => state.cms.pages.pagination;

export const selectCmsLoading = (
  state: CmsRootState,
): Record<CmsLoadingKey, boolean> => state.cms.loading;

export const selectCmsError = (
  state: CmsRootState,
): Record<CmsLoadingKey, string | null> => state.cms.error;

// -- Editor selectors (CON-CMS-002) -----------------------------------------

export const selectEditorPage = (state: CmsRootState) => state.cms.editor.page;

export const selectEditorRevisions = (state: CmsRootState) =>
  state.cms.editor.revisions;

export const selectEditorLoading = (state: CmsRootState) => ({
  fetchPage: state.cms.loading.fetchPage,
  createPage: state.cms.loading.createPage,
  savePage: state.cms.loading.savePage,
  fetchRevisions: state.cms.loading.fetchRevisions,
});

export const selectEditorErrors = (state: CmsRootState) => ({
  fetchPage: state.cms.error.fetchPage,
  createPage: state.cms.error.createPage,
  savePage: state.cms.error.savePage,
  fetchRevisions: state.cms.error.fetchRevisions,
});
